import { IconButton, twClassMerge  } from '@healthhub/react-ui';
import type { InputProps, InputRef } from 'antd';
import { Divider, Input } from 'antd';
import { forwardRef, memo } from 'react';

import { OutlinedCloseIcon, PortalOutlinedSearchIcon } from '@shared/assets-icon';


import styles from './PortalSearchInput.module.css';
import { PORTAL_SEARCH_INPUT_VARIANT } from './PortalSearchInput.variant';

interface PortalSearchInputProps extends Pick<InputProps, 'className' | 'onChange' | 'onKeyDown'> {
  'data-testid'?: string;
  onClickClear?: () => void;
  onClickSearch?: () => void;
  placeholder?: string;
  value: string;
}

const PortalSearchInput = forwardRef<InputRef, PortalSearchInputProps>(
  (
    {
      onChange,
      onKeyDown,
      placeholder,
      value,
      className,
      'data-testid': dataTestId,
      onClickSearch,
      onClickClear,
    }: PortalSearchInputProps,
    ref
  ) => {
    const onClickClearButton = () => {
      if (onClickClear) {
        onClickClear();
      } else {
        onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
      }
    };

    const { inputClass, suffixWrapperClass, clearIconClass, searchIconClass, dividerClass } =
      PORTAL_SEARCH_INPUT_VARIANT();

    const label = placeholder ?? 'Search';

    return (
      <Input
        aria-label={label}
        className={twClassMerge(inputClass(), styles.input, className)}
        data-testid={dataTestId}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={label}
        ref={ref}
        role="searchbox"
        suffix={
          <div className={suffixWrapperClass()}>
            {!!value && (
              <>
                <IconButton
                  aria-label="Clear input"
                  icon={<OutlinedCloseIcon className={twClassMerge(clearIconClass(), styles.clearIcon)} />}
                  onClick={onClickClearButton}
                />
                <Divider className={dividerClass()} type="vertical" />
              </>
            )}

            {onClickSearch ? (
              <IconButton
                aria-label="Search"
                icon={<PortalOutlinedSearchIcon className={twClassMerge(searchIconClass(), styles.searchIcon)} />}
                onClick={onClickSearch}
              />
            ) : (
              <PortalOutlinedSearchIcon
                aria-hidden="true"
                className={twClassMerge(searchIconClass(), styles.searchIcon)}
              />
            )}
          </div>
        }
        value={value}
      />
    );
  }
);

export default memo(PortalSearchInput);
